// This file defines all types related to the "Worksheet" document type.
// See comment on WorksheetType for details.

import type DeltaStatic from "quill-delta";
import { Doc, Op } from "sharedb/lib/client";

import { KernelStateType } from "../worksheets/code-kernels";

import type { OEmbedType } from "./oembed";

export type ExtendedOp = Op & {
  operationId: string;
  op: ExtendedOp[];
  retries: number;
  sentAt: number;
  originalSentAt: number;
  src: string;
  seq: number;
};

export const HARD_ROLLBACK_STATUS_EVENT = "hard rollback status";
export type HardRollbackStatus = "recovering" | "recovered" | "failed";

export interface ExtendedDoc extends Doc<WorksheetType> {
  hardRollbackStatus?: HardRollbackStatus;
  inflightOp: ExtendedOp;
  pendingOps: ExtendedOp[];
  _hardRollback: (error: Error) => void;
  __hardRollback: (error: Error) => void;
  flush: () => void;
  _flush: () => void;
  _sendOp: () => void;
  _clearInflightOp: () => void;
}

/**
 * WorksheetType is a custom format for a JSON document used through ShareDB/MongoDB.
 *
 * The structure of a worksheet is defined by Blocks, which is the unit of content that a user
 * can add/remove from a Worksheet. Blocks are made up of Fields, which is a unit of content
 * that is tied to some specific functionality.
 * E.g. a Code Block is made up of a TextField and a CodeField. The TextField is rendered
 * as a text editor for instructors to provide instructions, and the CodeField is rendered
 * as a code editor where users can enter/modify code.
 *
 * All frequently accessed property names are shortened to single characters to reduce the
 * size of the JSON payload. We send long property chains on almost every keystroke of the user,
 * so this is a significant optimization, despite reducing readability. Check the docstrings for
 * the full property names and explanations.
 */
export type WorksheetType = {
  /**
   * List of blocks. See BlockType for details.
   */
  b: BlockType[];
  /**
   * User content for each field. See FieldDataType for details.
   */
  d: FieldDataType;
  /**
   * If the worksheet is locked, students will not be able to edit it.
   */
  // TODO: This should be controlled by Django's "locked_at" fields, but isn't yet
  locked?: boolean;
};

/**
 * The structure of a worksheet. Each Block contains a list subcomponents called Fields.
 * Fields link to specific functionality and UI components.
 * E.g. a Code Block is made up of a TextField and a CodeField. The TextField is rendered
 * as a text editor for instructors to provide instructions, and the CodeField is rendered
 * as a code editor where users can enter/modify code.
 *
 * Fields may also contain default content that prefills user's data the first time
 * they start filling in a field.
 */
export type BlockType = { id: string } & (
  | InstructionsBlockType
  | FreeResponseBlockType
  | MultipleChoiceBlockType
  | TableBlockType
  | FileBlockType
  | EmbedBlockType
  | CodeBlockType
  | AssetBlockType
  | WhiteboardBlockType
);

export type BlockVariantType = BlockType["t"];

export type FieldType =
  | InstructionsFieldType
  | FreeResponseFieldType
  | ChoiceFieldType
  | TableFieldType
  | EmbedFieldType
  | FileFieldType
  | AssetFieldType
  | CodeFieldType
  | WhiteboardFieldType;

export interface InstructionsBlockType {
  /**
   * Type. The type of block.
   */
  t: "Instructions";
  f: [InstructionsFieldType];
}

export interface FreeResponseBlockType {
  /**
   * Type. The type of block.
   */
  t: "FreeResponse";
  f: [InstructionsFieldType, FreeResponseFieldType];
}

export interface MultipleChoiceBlockType {
  /**
   * Type. The type of block.
   */
  t: "MultipleChoice";
  f: [InstructionsFieldType, ChoiceFieldType];
}

export interface TableBlockType {
  /**
   * Type. The type of block.
   */
  t: "Table";
  f: [TableFieldType] | [InstructionsFieldType, TableFieldType];
}

export interface FileBlockType {
  /**
   * Type. The type of block.
   */
  t: "File";
  f: [InstructionsFieldType, FileFieldType];
}

export interface EmbedBlockType {
  /**
   * Type. The type of block.
   */
  t: "Embed";
  f: [EmbedFieldType];
}

export interface CodeBlockType {
  /**
   * Type. The type of block.
   */
  t: "Code";
  f: [CodeFieldType];
}

export interface AssetBlockType {
  // TODO: This should be called "FileUpload" to differentiate it from "Asset"
  // but then we'd need to rename the "File" field too
  /**
   * Type. The type of block.
   */
  t: "Asset";
  f: [AssetFieldType];
}

export interface WhiteboardBlockType {
  /**
   * Type. The type of block.
   */
  t: "Whiteboard";
  f: [WhiteboardFieldType];
}

export interface ScrollDimensionsType {
  /**
   * The current x position of the scrollbar in the element.
   */
  scrollX?: number;
  /**
   * The current y position of the scrollbar in the element.
   */
  scrollY?: number;
  /**
   * The total scrollable width of the current element.
   */
  scrollWidth?: number;
  /**
   * The total scrollable height of the current element.
   */
  scrollHeight?: number;
  /**
   * The current client width of the element.
   */
  clientWidth?: number;
  /**
   * The current height of the element.
   */
  clientHeight?: number;
}

export interface PdfState extends ScrollDimensionsType {
  /**
   * The current zoom level of the PDF.
   */
  zoom: number;
  /**
   * The current page number of the PDF.
   */
  pageNumber: number;
  /**
   * Total number of pages in the PDF.
   */
  totalPages: number;
  /**
   * Whether the state of the PDF has been overwritten by the user when following.
   */
  userUpdated?: boolean;
}

export interface VideoState {
  /**
   * The current playback state of the video.
   */
  playing: boolean;
  /**
   * The current playback time of the video, in seconds.
   */
  currentTimeInSeconds: number;
  /**
   * The playback rate of the video.
   */
  playbackRate?: number;
}

/**
 * This type is for fields that contain instructions to the students.
 * The expectation is that students will never edit these fields; only instructors will do so,
 * while creating/editing a page.
 */
export interface InstructionsFieldType {
  /**
   * Type. The type of field.
   */
  t: "Text";
  id: string;
  /**
   * Version. Incremented on semantic updates to the field.
   */
  ver: number;
  /**
   * Value. The display value of the field.
   */
  v: RichTextCellType;
}

/** This type is for fields that students can type into, in order to respond to a prompt.
 * If a FreeResponseFieldType is present on a page, that automatically means that the page contains
 * interactive content for students. Instructors can also set seeded content on these fields,
 * to help students get started with their responses.
 */
export interface FreeResponseFieldType {
  /**
   * Type. The type of field.
   */
  t: "RichTextField";
  maxLength: number | null;
  id: string;
  /**
   * Version. Incremented on semantic updates to the field.
   */
  ver: number;
  /**
   * Value. The display value of the field.
   */
  v?: RichTextCellType;
}

export interface ChoiceFieldChoiceType {
  /**
   * Label. The display value of the choice.
   */
  l: string;
  /**
   * Value. The state value of the choice.
   */
  v: string;
  id: string;
}

export interface ChoiceFieldType {
  /**
   * Type. The type of field.
   */
  t: "ChoiceField";
  id: string;
  /**
   * Version. Incremented on semantic updates to the field.
   */
  ver: number;
  /**
   * Multiple. Whether multiple choices can be selected.
   */
  m: boolean;
  /**
   * what type of chart type do we use "bar" or "pie".
   */
  ct?: "bar" | "pie";
  /**
   * Choices. List of choices.
   */
  c: ChoiceFieldChoiceType[];
}

export interface TableFieldType {
  /**
   * Type. The type of field.
   */
  t: "TableField";
  id: string;
  /**
   * Version. Incremented on semantic updates to the field.
   */
  ver: number;
  /**
   * Value. The full content of the table.
   */
  v: TableDimensionsType;
}

export interface FileFieldType {
  /**
   * Type. The type of field.
   */
  t: "FileField";
  id: string;
  /**
   * Version. Incremented on semantic updates to the field.
   */
  ver: number;
  /**
   * Multiple. Whether multiple files can be uploaded.
   */
  m: boolean;
}

export type OEmbedDataType = {
  url: string;
  o: OEmbedType;
};

export interface EmbedFieldType extends OEmbedDataType {
  /**
   * Type. The type of field.
   */
  t: "EmbedField";
  id: string;
  /**
   * Version. Incremented on semantic updates to the field.
   */
  ver: number;
  videoState?: VideoState;
}

export interface WhiteboardFieldType {
  /**
   * Type. The type of field.
   */
  t: "WhiteboardField";
  id: string;
  /**
   * Version. Incremented on semantic updates to the field.
   */
  ver: number;
}

/** @url https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/track */
export interface Track {
  src: string;
  srcLang: "en" | "es";
  label: string;
  kind?: "subtitles" | "captions" | "descriptions";
}

export interface AssetFieldType {
  /**
   * Type. The type of field.
   */
  t: "AssetField";
  id: string;
  /**
   * Version. Incremented on semantic updates to the field.
   */
  ver: number;
  url: string | null;
  size: string;
  name: string;
  /**
   * FileType. The MIME type of the file.
   */
  fileType: string;
  view: "inline" | "condensed";
  /**
   * Annotations. Whether the whiteboard is enabled for this asset.
   */
  annotations?: boolean;
  /** Used for image accessibility purposes. */
  alt?: string;
  /** Decorative. If set to true, the `alt` text should be ignored. */
  dec?: boolean;
  /** Used for video captions/subtitles. */
  tracks?: Track[];
  /**
   * The following fields are for syncing student PDFs/videos with the instructor view.
   */
  pdfState?: PdfState;
  videoState?: VideoState;
}

export interface CodeFieldType extends CodeCellType {
  /**
   * Type. The type of field.
   */
  t: "CodeField";
  id: string;
  /**
   * Version. Incremented on semantic updates to the field.
   */
  ver: number;
  cellType: "editable" | "readonly" | "hidden";
}

export type RichTextCellType = { ops: DeltaStatic["ops"] };

export type CodeCellTypeOption = "editable" | "readonly" | "hidden";

export interface CodeCellPromptType {
  isSubmitted?: boolean;
  rawJSON?: string;
  /**
   * Value. The string content of the user's input for this prompt.
   */
  v?: string;
}

export interface CodeCellOutputDataType {
  /**
   * Data. Arbitrary data that is associated with this output.
   */
  data?: object;
  metadata?: object;
  name: string;
  output_type: string;
  text: string;
}

export interface CodeCellOutputSourcePathType {
  /**
   * FilePath. The URL for the file that contains the output data.
   */
  fp: string;
}

export type CodeCellOutputSource = CodeCellOutputSourcePathType | CodeCellOutputDataType;

export interface CodeCellPagerDataType {
  /**
   * Data. Jupyterhub-given data that is associated with this pager.
   */
  d: object;
  metadata: object;
}

export interface CodeCellPagerSourcePathType {
  /**
   * FilePath. The URL for the file that contains the pager data.
   */
  fp: string;
}

export type CodeCellPagerSourceType = CodeCellPagerSourcePathType | CodeCellPagerDataType;

export type CodeCellType = {
  /**
   * Code. The actual code that is stored in this cell.
   */
  c: string;
  /**
   * Outputs. List of outputs that are associated with this code cell.
   */
  o: (CodeCellOutputSource | string)[];
  /**
   * Pagers. List of pagers that are associated with this code cell.
   */
  p: (CodeCellPagerSourceType | string)[];
  /**
   * Current prompt. This is populated if Jupyterhub is expecting STDIN input for this code
   * cell to execute, and contains information about the prompt.
   */
  cp?: CodeCellPromptType;
  /**
   * Execution count. Number of times this specific cell has been executed.
   */
  ec?: number;
  /**
   * Execution sequence number. Jupyterhub increments this number every time a cell is executed,
   * so that we can keep track of the order of execution.
   */
  esn?: number;
};
export type MultipleChoiceCellType = number[];
export type TableCellId = `${string},${string}`;
export type TableCellsMapType = Record<TableCellId, TableCellType>;
/**
 * Unlike most other fields, TableDimensionsType is used both for the field definition in the blocks,
 * as well as student data. Right now, we don't allow students to resize tables or change cell properties
 * (like per-cell "readOnly"), but the structure supports adding those features in the future.
 */
export type TableDimensionsType = {
  /**
   * An array of row ids.
   * This represents the ids for each row in the table.
   * NOTE: Only used when in block definition (doc.data.b), specified but unused
   *  when in student data (doc.data.d) outside class-collaboration mode.
   */
  r: string[];
  /**
   * An array of column ids.
   * this represents the ids for each column in the table.  As c can
   * also be thought of as cells we call the cell map below cm
   * NOTE: Only used when in block definition (doc.data.b), specified but unused
   *  when in student data (doc.data.d) outside class-collaboration mode.
   */
  c: string[];
  /**
   * A sparse map of cell data.  `cm` is for cell map.
   * The keys are the cell ids, which are a combination of the row and column ids.
   * If a cell is not present in the map, it is considered empty.
   */
  cm: TableCellsMapType;
};
export type TableCellType = {
  /**
   * ReadOnly. Whether this cell is student-editable.
   */
  ro?: boolean;
  /**
   * Value. The text content of the cell.
   */
  v?: RichTextCellType;
};
export type FileCellType = FileDataType[];
export type FileDataType = {
  /**
   * Type. The type of file.
   */
  t: string;
  filename: string;
  size: string; // "x kB"
  id: string;
  url: string;
  placeholder?: boolean;
};

export type EmbedCellType = {
  url: string;
};

export type SeededCellType = {
  initialized: boolean;
} & ({ v: RichTextCellType } | CodeCellType);

export type CellType =
  | CodeCellType
  | RichTextCellType
  | MultipleChoiceCellType
  | TableDimensionsType
  | FileCellType
  | EmbedCellType
  | SeededCellType;

export type PathType = (string | number)[];

/**
 * Metadata for each user or group's Page data, such as kernel information.
 * This is stored directly at the AccessId level, so it is specific to each user or group.
 * - Versions is a dictionary that maps field IDs to their current (or last seen) version number.
 * - KernelState is a string that indicates the status of the kernel for the user or group. For a list of states
 *   see `code-kernels.ts`.
 * - RunningCell is a list of strings that indicates the path to the cell that is currently running.
 * - RunningCellStartedAt is a number that indicates the time at which the running cell started.
 * NOTE if you add fields here, also modify ACCESS_ID_DATA_META_FIELDS in constants.ts
 */
export interface AccessIdMetaDataType {
  versions: Record<string, number>;
  // Code-related
  kernelState?: KernelStateType;
  runningCell?: PathType;
  runningCellStartedAt?: number;
}

/**
 * Defines each user or group's (based on accessId) content for every Field of a worksheet.
 * Defines metadata for each user or group's Page data, such as kernel information.
 */
export type AccessIdDataType = AccessIdMetaDataType & {
  [k: string]: CellType;
};

/**
 * Defines each user or group's (based on accessId) content for every Field of a worksheet.
 * Defines metadata for each user or group's Page data, such as kernel information.
 * Structured as a nested dictionary with keys defined as:
 * UserAccessId => FieldId => FieldContent (also called "Cell")
 * To learn more about AccessIds, see `access-id.ts`.
 */
export type FieldDataType = Record<string, AccessIdDataType | undefined>;
export type PinsType = { [key: string]: boolean };
